import * as z from "@-/com.npmjs.zod";
import { morphemeDetailsTileSliceSchema } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { filterNullable } from "@-/com.npmjs.array-utils-ts";
import { getMorphemeDetailsTileSlices } from "@-/dev.improve.haniverse.lib.morpheme.read/details";
import { morphemeCorpusFilterFromCacheKey } from "@-/dev.improve.haniverse.lib.morpheme.corpus";
import {
  isSentenceSearchQuery,
  SEARCH_PAGE_SIZE,
  searchMorphemes,
  searchResultToMorphemeLookup,
} from "#search-morphemes-core";
import { getSentenceSearchPage } from "#search-sentences";

export const searchPageRequestSchema = z.object({
  category: z.enum(["all", "characters", "words", "sentences"]).default("all"),
  /**
   * The app user's `MorphemeCorpusFilter.cacheKey`. Absent for callers with no signed-in
   * settings (e.g. an anonymous or scripted request), which get the unfiltered dictionary.
   */
  corpus: z.string().max(1024).optional(),
  /** See `SearchMorphemesOptions`. */
  exactOnly: z.boolean().default(false),
  offset: z.int().min(0).max(100_000).default(0),
  /** See `SearchMorphemesOptions`: absent, pinyin is detected from the query. */
  pinyin: z.boolean().optional(),
  query: z.string().trim().min(1).max(256),
  script: z.enum(["simplified", "traditional"]).default("simplified"),
});

export const searchPageResponseSchema = z.object({
  allMorphemes: z.array(
    z.object({
      category: z.enum(["character", "word"]),
      front: z.string().nullable(),
      id: z.int(),
      matchedText: z
        .object({
          pinyin: z.string().nullable(),
          source: z.enum(["definition", "gloss"]),
          text: z.string(),
        })
        .nullable(),
      // A Worker deployed before partial matches returns exact matches only.
      matchKind: z.enum(["exact", "partial"]).default("exact"),
    }),
  ),
  cards: z.array(morphemeDetailsTileSliceSchema),
  hasMoreSentences: z.boolean().default(false),
  hitCount: z.int().min(0),
  nextOffset: z.int().min(0),
  offset: z.int().min(0),
  sentences: z
    .array(z.object({ id: z.int(), sentence: z.string(), translation: z.string().nullable() }))
    .default([]),
});

export type SearchPage = z.infer<typeof searchPageResponseSchema>;
export type SearchPageRequest = Omit<
  z.infer<typeof searchPageRequestSchema>,
  "exactOnly" | "script"
> & {
  exactOnly?: boolean;
  script?: "simplified" | "traditional";
};

/** Shared by the Worker and tests; only public content and static dictionary data are needed. */
export async function getSearchPage(
  contentDb: Parameters<typeof searchMorphemes>[0],
  staticDb: Parameters<typeof searchMorphemes>[1],
  { category, corpus, exactOnly, offset, pinyin, query, script }: SearchPageRequest,
): Promise<SearchPage> {
  if (category === "sentences") {
    const page = await getSentenceSearchPage(staticDb, { offset, query, script });
    return { ...page, allMorphemes: [], cards: [], hitCount: page.sentences.length, offset };
  }
  const allMorphemes = await searchMorphemes(
    contentDb,
    staticDb,
    query,
    corpus === undefined ? undefined : morphemeCorpusFilterFromCacheKey(corpus),
    { exactOnly, pinyin },
  );
  const effectiveCategory = isSentenceSearchQuery(query) ? "all" : category;
  const hits =
    effectiveCategory === "all"
      ? allMorphemes
      : allMorphemes.filter((hit) => (hit.category === "word") === (effectiveCategory === "words"));
  const nextOffset = Math.min(offset + (offset === 0 ? 12 : SEARCH_PAGE_SIZE), hits.length);
  const cards = filterNullable(
    await getMorphemeDetailsTileSlices(
      contentDb,
      staticDb,
      hits.slice(offset, nextOffset).map((hit) => searchResultToMorphemeLookup(hit)),
    ),
  );
  return {
    allMorphemes,
    cards,
    hasMoreSentences: false,
    hitCount: hits.length,
    nextOffset,
    offset,
    sentences: [],
  };
}
